/**
 * The phone's button.
 *
 * The shared `Button` primitive is 32px tall — right for a dense desktop
 * table, too small for a thumb on a phone propped at arm's length. This keeps
 * the app's colours, border and radius exactly, and only changes the size to
 * meet the 44px minimum touch target.
 */
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";

export function PhoneButton({
  variant = "primary",
  icon,
  className,
  children,
  type = "button",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "danger";
  icon?: ReactNode;
}) {
  const variants = {
    primary:
      "bg-accent text-white border-accent active:bg-accent-hover active:border-accent-hover",
    secondary:
      "bg-surface text-ink border-border-strong active:bg-surface-sunken",
    danger:
      "bg-surface text-violation border-violation/40 active:bg-violation-soft",
  } as const;

  return (
    <button
      type={type}
      className={cn(
        "inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md border",
        "px-4 text-sm font-medium transition-colors select-none",
        "disabled:pointer-events-none disabled:opacity-50",
        variants[variant],
        className,
      )}
      {...rest}
    >
      {icon ? <span className="shrink-0 [&_svg]:size-4">{icon}</span> : null}
      {children}
    </button>
  );
}
